'use strict';

/**
 * Handles listening for events and pushing data to the GA dataLayer
 *
 * Example: This adds a listener for "click" events for `button` elements in the `div` and pushes
 * data to the dataLayer with the event name `button_click` and the data `{page_title: "My Page"}`
 * ```html
 * <div
 *  data-dl-action="button_click"
 *  data-dl-on="click"
 *  data-dl-target="button"
 *  data-dl-data="{&quot;page_title&quot;: &quot;My Page&quot;}"
 * >
 *  <button>Click Me</button>
 * </div>
 * ```
 * 
 * @typedef {Object<string, unknown>} DataLayerData Data to be pushed to GA dataLayer
 */
class DataLayer {
    /**
     * @param {HTMLElement} [el]
     */
    constructor(el) {
        /**
         * Object to be pushed to GA dataLayer
         * @type {DataLayerData}
         */
        this.data = {};

        if (el && el.nodeType) {
        this.constructByElement(el);
        }
    }

    /**
     * Construct by element
     * @param {HTMLElement} el
     * @returns {void}
     */
    constructByElement(el) {
        /** @type {HTMLElement} */
        this.el = el;

        /** @type {Event} */
        this.event = null; // contents of the click/submit/etc event

        /** @type {string} */
        this.on = el.dataset.dlOn;

        /** @type {boolean} */
        this.defer = el.dataset.dlDefer;

        /** @type {string} */
        this.action = el.dataset.dlAction;

        /** @type {string} */
        this.initAction = el.dataset.dlInitAction;

        /** @type {string} */
        this.targetSelector = el.dataset.dlTarget;

        /** @type {string} */
        this.message = el.dataset.dlMessage;

        if (this.initAction) {
        // If the element has a `data-dl-init-action` attribute,
        // use that action for the initial event, then revert to the data-dl-action attribute
        this.action = this.initAction;
        this.handleEvent();
        this.action = el.dataset.dlAction;
        }

        if (this.on && this.defer) {
        return this.el.addEventListener(this.on, this.handleDeferred.bind(this));
        }

        if (this.on) {
        // Add event listener to target elements if [data-dl-on] is set
        return this.getTargetElements().forEach(target => {
            target.addEventListener(this.on, this.handleEvent.bind(this));
        });
        }

        if (this.message) {
        // Listens for `MessageEvent` if [data-dl-message] is set and handles it if the message matches
        return window.addEventListener('message', event => {
            if (event.data.message === this.message) {
            this.handleMessageEvent(event);
            }
        });
        }

        this.handleEvent();
    }

    /**
     * Handle events
     * @param {Event} [event]
     */
    handleEvent(event) {
        this.event = event;
        if (this.getData() && this.action) {
        // Only push to dataLayer if there is an action attached to this event
        this.push();
        }
    }

    /**
     * Handle deferred events
     * @param {Event} event
     */
    handleDeferred(event) {
        if (this.getTargetElements().includes(event.target)) {
        this.handleEvent(event);
        }
    }

    /**
     * Handle message events
     * @param {MessageEvent} event
     */
    handleMessageEvent(event) {
        // Example for handling different messages
        switch (event.data.message) {
        case 'formLoaded':
            break;
        default:
        }

        if (this.action) {
        this.handleEvent(event);
        this.reset();
        }
    }

    /**
     * Get an array of target elements
     * @returns {Array<HTMLElement>}
     */
    getTargetElements() {
        return this.targetSelector
        ? Array.from(this.el.querySelectorAll(this.targetSelector))
        : [this.el];
    }

    /**
     * Construct the data object based on the action being listened for
     * @returns {boolean}
     */
    getData() {
        /** Element that triggered the event */
        const eventTarget = this.event?.target || null;
        /** Element to which the event listener is attached */
        const currentTarget = this.event?.currentTarget || null;

        /** Merge the [data-dl-data] attributes from the parent element and targets */
        this.mergeData([this.el, currentTarget, eventTarget]);

        this.data.event = this.action;

        switch (this.action) {
        case 'nav_click':
            this.data.click_text = currentTarget?.innerText;
            this.data.click_url = currentTarget?.href;
            break;
        default:
        }

        return true;
    }

    /**
     * Merge [data-dl-data] from elements into the data object
     * @param {HTMLElement | Array<HTMLElement>} els
     */
    mergeData(els) {
        // if elements is not an array, make it an array
        const elements = Array.isArray(els) ? els : [els];

        elements.forEach(el => {
        if (el?.dataset?.dlData) {
            const data = JSON.parse(el.dataset.dlData);
            if (data) {
            this.data = Object.assign(this.data, data);
            }
        }
        });
    }

    /**
     * Lowercase all the values in the data object
     * @param {DataLayerData} data
     * @returns {DataLayerData}
     */
    lowercaseValues(data) {
        Object.keys(data).forEach(key => {
        if (typeof data[key] === 'string') {
            data[key] = data[key].toLowerCase();
        }
        if (typeof data[key] === 'object') {
            this.lowercaseValues(data[key]);
        }
        });
        return data;
    }

    /**
     * Push data to GA dataLayer
     * @param {DataLayerData} [data]
     */
    push(data) {
        let dataLayerData = data || this.data;
        this.log(dataLayerData);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerData);
    }

    /**
     * Log dataLayer pushes to the console in dev mode
     * @param {DataLayerData} data
     */
    // eslint-disable-next-line class-methods-use-this
    log(data) {
        if (window.location.host.split(':')[0] !== 'localhost') {
          return;
        }
        // eslint-disable-next-line no-console
        console.log('dataLayer.push:', data);
    }

    /**
     * Reset the dataLayer module
     */
    reset() {
        this.action = null;
        this.data = {};
    }
}

/**
 * Creates a new dataLayer event without the need to be attached to an element
 */
class DataLayerEvent {
    /**
     * Constructs a new DataLayerEvent
     * @param {string} eventName Name of GA event
     * @param { import("./DataLayer").DataLayerData } data
     */
    constructor(eventName, data) {
        const dl = new DataLayer();
        dl.action = eventName;
        dl.data = data;
        if (!dl.getData()) {
        return;
        }
        dl.push();
    }
}

var NSF = {
    countUp: {
        isElementInViewport: function(elem) {
            var docViewTop = $(window).scrollTop();
            var docViewBottom = docViewTop + $(window).height();

            var verticalOffset = 0;
            var elemTop = $(elem).offset().top - verticalOffset;
            var elemBottom = elemTop + $(elem).outerHeight();

            var isVisible = ((elemBottom > docViewTop) && (elemTop < docViewBottom));

            return isVisible;
        },
        animateCount: function($target){
            var count = 0;
            var finalValue = parseInt($target.data('count'), 10);
            var increment = parseInt((finalValue * 15 / 1250), 10);

            increment = increment === 0 ? 1 : increment;

            var countUp = setInterval(function(){
                count += increment;
                count = count > finalValue ? finalValue : count;

                $target.html(count);

                if (count >= finalValue) {
                    clearInterval(countUp);
                }
            }, 15);
        },
        init: function(){
            var that = this;

            $('[data-count]').each(function(){
                if(that.isElementInViewport($(this)[0])) {
                    that.animateCount($(this));
                    $(this).removeAttr('data-count');

                    // $(this).closest('section').addClass('k-animated');
                }
            });
        }
    },
    toggles: {
        init: function() {
            $('[data-toggle]').on('click', function(){
                $($(this).data('toggle')).slideToggle();
                $(this).toggleClass('active');
            });
        }
    },
    contactForm: {
        init: function() {
            $('#contactForm').submit(function(ev) {
                ev.preventDefault();

                var $form = $(this);
                var $submitButton = $form.find('input[type="submit"]');

                $form.find('input#subject').val('Message from ' + $form.find('#firstName').val() + ' ' + $form.find('#lastName').val());
                $submitButton.attr('disabled', 'disabled');

                $.post({
                    url: '/',
                    dataType: 'json',
                    data: $(this).serialize(),
                    success: function(response, b, c) {
                        if (response.success) {
                            $form.find('#successMessage').fadeIn();
                        }
                    },
                    error: function(a,b,c) {
                        $form.find('#errorMessage').fadeIn();
                        console.log('a: ', a);
                        console.log('b: ', b);
                        console.log('c: ', c);
                    }
                });
            });
        }
    },
    equalCardHeights: {
        init: function() {
            $('[data-cards]').each(function(){
                var $cardsGroup = $(this);
                var $cards = $cardsGroup.find('[data-card]');
                var height = 0;

                $cards.each(function(){
                    var h = $(this).find('[data-card-copy]').height();

                    if (h > height) {
                        height = h;
                    }
                });

                $cards.each(function(){
                    $(this).find('[data-card-copy]').height(height + 'px');
                });
            });
        }
    },
    dataLayer: {
        /** Creates a new datalayer module for each element using it */
        init: function() {
            document.querySelectorAll('[data-dl-action]').forEach(el => {
                new DataLayer(el);
            })
        },
    },
}

$(function(){
    NSF.countUp.init();
    NSF.toggles.init();
    NSF.contactForm.init();
    NSF.equalCardHeights.init();
    NSF.dataLayer.init();

    var resizeTimer;

    $(window).on('scroll', function(e) {
        clearTimeout(resizeTimer);

        resizeTimer = setTimeout(function() {
            NSF.countUp.init();
        }, 50);
    });
});
